import { useCallback, useMemo, useRef } from "react";
import PropTypes from "prop-types";
// auth
import { useAuthContext } from "src/auth/hooks";

// @mui
import Container from "@mui/material/Container";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Unstable_Grid2";

// locales
import { useLocales } from "src/locales";

// components
import { Helmet } from "react-helmet-async";
import DataGrid from "src/consuman-components/generic/dataGrid";
import Column from "src/consuman-components/generic/column";
import { HeaderFilter } from "devextreme-react/data-grid";

// format
import { dateTimeFormatGrid } from "src/core/lib/formats";

// api
import { getEntityGrid } from "src/api/audit";

// utils
import { stateDataSource, buildStateText, buildUserText } from "./utils";

// ----------------------------------------------------------------------

export default function AuditEntityGrid({ entityName, entityId }) {
  const { t } = useLocales();
  const { user } = useAuthContext();
  const dataGridRef = useRef(null);

  const states = useMemo(
    () => stateDataSource.map(s => ({ value: s.value, text: t(s.text) })),
    [t]
  );

  const customizeUserText = useCallback(cellInfo => buildUserText(cellInfo.value, t), [t]);
  const customizeStateText = useCallback(rowData => buildStateText(rowData, true, t), [t]);

  return (
    <>
      <Helmet>
        <title> {t("audit")} </title>
      </Helmet>

      <Container maxWidth="lg">
        <Grid
          xs={12}
          md={8}
        >
          <Card>
            <DataGrid
              remoteOperation
              height={600}
              innerRef={dataGridRef}
              scrolling="infinite"
              dataSource={getEntityGrid(entityName, entityId)}
              nameGrid={t("audit")}
              keyExpr="id"
            >
              <Column
                dataField="date"
                sortOrder="desc"
                caption={t("date")}
                format={dateTimeFormatGrid(user.dateTimeFormat)}
                dataType="date"
                allowGrouping={false}
              />

              <Column
                dataField="user"
                caption={t("user")}
                dataType="string"
                customizeText={customizeUserText}
              />

              <Column
                dataField="state"
                caption={t("state")}
                dataType="string"
                allowSorting={false}
                allowSearch={false}
                allowFiltering={false}
                allowHeaderFiltering
                calculateDisplayValue={customizeStateText}
              >
                <HeaderFilter
                  allowSearch={false}
                  dataSource={states}
                />
              </Column>
            </DataGrid>
          </Card>
        </Grid>
      </Container>
    </>
  );
}

AuditEntityGrid.propTypes = {
  entityName: PropTypes.string,
  entityId: PropTypes.string,
};
